import { useId } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormField } from '@/components/app';
import { EditUserDialogProps } from '../types';

export function EditUserDialog({
  isOpen,
  onOpenChange,
  formData,
  onFormDataChange,
  onSave,
  isSaving = false,
  isSelf = false,
}: EditUserDialogProps) {
  const missingEnd = !!formData.isSubscribed && !formData.subscribedEnd;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg gap-0 p-0">
        <DialogHeader bordered>
          <DialogTitle>Редактировать пользователя</DialogTitle>
          <DialogDescription>Изменения попадут в аудит-лог.</DialogDescription>
        </DialogHeader>
        <form
          id="edit-user-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!isSaving) void onSave();
          }}
        >
          <DialogBody>
            <FormField label="Email">
              <Input
                type="email"
                value={formData.email || ''}
                onChange={(e) => onFormDataChange((prev) => ({ ...prev, email: e.target.value }))}
              />
            </FormField>
            <FormField label="Логин">
              <Input
                type="text"
                value={formData.username || ''}
                onChange={(e) => onFormDataChange((prev) => ({ ...prev, username: e.target.value }))}
              />
            </FormField>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField label="Имя">
                <Input
                  type="text"
                  value={formData.firstName || ''}
                  onChange={(e) => onFormDataChange((prev) => ({ ...prev, firstName: e.target.value }))}
                />
              </FormField>
              <FormField label="Фамилия">
                <Input
                  type="text"
                  value={formData.lastName || ''}
                  onChange={(e) => onFormDataChange((prev) => ({ ...prev, lastName: e.target.value }))}
                />
              </FormField>
            </div>
            <FormField
              label="Роль"
              hint={isSelf ? 'Свою роль менять нельзя: так можно потерять доступ к админке.' : 'Роль «Админ» даёт полный доступ к админ-панели — потребуется подтверждение.'}
            >
              {(p) => (
                <Select
                  value={formData.role}
                  disabled={isSelf}
                  onValueChange={(value: 'user' | 'admin') => onFormDataChange((prev) => ({ ...prev, role: value }))}
                >
                  <SelectTrigger {...p}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">Пользователь</SelectItem>
                    <SelectItem value="admin">Админ</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </FormField>

            <SwitchRow
              label="Подписка Premium"
              checked={!!formData.isSubscribed}
              onChange={(v) => onFormDataChange((prev) => ({ ...prev, isSubscribed: v }))}
            />
            {formData.isSubscribed && (
              <FormField
                label="Действует до (время Ташкента)"
                required
                error={missingEnd ? 'Укажите дату окончания подписки' : undefined}
                hint="Без даты приложение не считает подписку активной, а крон истечения её не видит"
              >
                <Input
                  type="datetime-local"
                  value={formData.subscribedEnd ?? ''}
                  onChange={(e) => onFormDataChange((prev) => ({ ...prev, subscribedEnd: e.target.value || null }))}
                />
              </FormField>
            )}
            <SwitchRow
              label="Заблокирован"
              hint={isSelf ? 'Себя заблокировать нельзя' : 'Пользователь не сможет войти в приложение'}
              checked={!!formData.isBlocked}
              disabled={isSelf}
              onChange={(v) => onFormDataChange((prev) => ({ ...prev, isBlocked: v }))}
            />
          </DialogBody>
        </form>
        <DialogFooter bordered>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Отмена
          </Button>
          <Button type="submit" form="edit-user-form" disabled={isSaving}>
            {isSaving && <Loader2 className="animate-spin" />}
            Сохранить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SwitchRow({
  label,
  hint,
  checked,
  onChange,
  disabled = false,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-border-soft bg-surface-alt px-3.5 py-3">
      <label htmlFor={id} className="flex flex-col gap-0.5 text-sm font-semibold text-text">
        {label}
        {hint && <span className="text-xs font-normal text-text-2">{hint}</span>}
      </label>
      <Switch id={id} checked={checked} onCheckedChange={onChange} disabled={disabled} />
    </div>
  );
}
